/**
 * Agent configuration model. Mirrors the Dataverse entity
 * `indskr_agentconfiguration` and the picklist values published on it.
 */

export enum AgentCategory {
  MeetingSummary          = 1,
  ContactProfileSummary   = 2,
  AccountProfileSummary   = 3,
  ContactSpotlightSummary = 4,
  Assistant               = 5,
  // Dedicated AI Summary card (factual prose brief) agents — separate rows from
  // the *ProfileSummary/*SpotlightSummary insights agents so each surface gets
  // its own dataSources + additionalInstructions (OMNI-59951).
  ContactSummary          = 6,
  AccountSummary          = 7,
  SpotlightSummary        = 8,
  /**
   * App-wide utility agent: features pass task instructions + a user message per
   * call (developer-role first turn) and get text or JSON back. Config rows for
   * this category typically set only `indskr_model` (direct-model mode) — no
   * hosted agent, no grounding.
   */
  GeneralPurpose          = 9,
}

export enum AgentHost {
  Foundry = 0,
}

/**
 * A skill bound to an agent config through the `indskr_agentskill_indskr_agentconfiguration`
 * N:N. `indskr_body` is deliberately absent: the grounding MCP resolves bodies server-side
 * from `agentConfigId`, so the device carries only what it sends itself.
 */
export interface AgentSkill {
  /** Dataverse record id (indskr_agentskillid). */
  id: string;
  name: string;
  /** Kebab-cased `name`. The token a rep types as `/<command>` in the assistant. */
  command: string;
  description?: string;
  /** Comma-separated match terms as authored (indskr_keywords). */
  keywords?: string;
  sortOrder?: number;
  /**
   * Whether the skill appears in the menu a rep browses with a bare `/` (indskr_showinmenu).
   * A skill set false stays bound, is still sent to the agent for routing, and still matches
   * when its command or name is typed.
   */
  showInMenu: boolean;
}

/**
 * Kebab-case a skill name into its `/command` token: accents folded, lowercased, and every
 * run of non-alphanumerics collapsed to one hyphen. `E2E — Oncology summary tone` becomes
 * `e2e-oncology-summary-tone`.
 */
export function toSkillCommand(name: string): string {
  return name
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export interface AgentConfig {
  /** Dataverse record id (indskr_agentconfigurationid). */
  id: string;
  /** Agent name as registered in the AI platform. */
  name: string;
  /** Project/host endpoint root (no trailing slash). */
  endpoint: string;
  category: AgentCategory;
  host: AgentHost;
  /**
   * On-device source codes this summary should assemble, from the `kind:'device'` entries in
   * `indskr_groundingconfig.sources`. Empty-vs-absent is significant:
   * `[]` = admin explicitly selected none; `undefined` = no grounding config (use the full
   * kind set). See AiDataSourceRegistry.resolve.
   */
  dataSources?: string[];
  /**
   * Raw grounding spec (indskr_groundingconfig JSON) sent to the server-side grounding agent
   * as the `groundingConfig` structured input. The forest carries both
   * `kind:'dynamics'` sources (the MCP queries them server-side) and `kind:'device'` sources
   * (client-resolved, with their data inline). Undefined ⇒ no grounding config on the row.
   */
  groundingConfig?: string;
  /** Agent instructions (indskr_instructions), sent as the Responses API `instructions` property. */
  instructions?: string;
  /** When set, the transport calls this model directly via {endpoint}/openai/v1 (no agent reference); empty ⇒ agent mode via the agent endpoint. */
  model?: string;
  /**
   * Dataverse MCP server id (indskr_dataversemcpserverid). When set, the transport sends it as the
   * `dataverseMcpServerId` structured input so the agent can target a specific Dataverse MCP server;
   * blank ⇒ the input is omitted and the agent falls back to its configured default.
   */
  dataverseMcpServerId?: string;
  /**
   * Security-role ids this config is restricted to (via indskr_securityroleassignment).
   * Set → the config is served only to users holding one of these roles, and it WINS
   * over the category's unrestricted config for them. Unset → default for everyone.
   */
  roleIds?: string[];
  /** Extra system-level instructions injected into every call to this agent (indskr_additionalinstructions). */
  additionalInstructions?: string;
  /** Skills bound to this config, in `indskr_sortorder`. Empty when none are bound. */
  skills: AgentSkill[];
}
